import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Wallet, AlertCircle, Banknote, Check, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(amount || 0);

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("user_token")}` };
}

/**
 * AgentPaymentFlow - Handles payment for petugas/agen with balance
 * Props:
 * - serviceType: "pdam" | "token_listrik" | "tagihan_listrik" | "pulsa"
 * - customerNo: string
 * - customerName: string
 * - customerAddress: string
 * - productName: string
 * - amount: number (tagihan amount)
 * - adminFee: number (fee loket)
 * - billDetails: array (for PDAM per-period details)
 * - buyerSkuCode: string
 * - onSuccess: function
 * - onCancel: function
 */
export function AgentPaymentButton({ serviceType, customerNo, customerName, customerAddress, productName, amount, adminFee, billDetails, billIds, buyerSkuCode, digiflazzRefId, returnPath, onSuccess, onReceiptClose, totalOverride }) {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [password, setPassword] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(""); // "cash" or "transfer"
  const [paymentMethodError, setPaymentMethodError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const userName = localStorage.getItem("user_name") || "";
  const userRole = localStorage.getItem("user_role") || "";
  const balance = parseFloat(localStorage.getItem("user_balance")) || 0;
  const total = totalOverride || ((amount || 0) + (adminFee || 0));
  
  // Petugas tidak perlu saldo - bisa bayar tanpa batasan saldo
  const isPetugas = userRole.toLowerCase() === "petugas";

  const handleConfirmPayment = async () => {
    // Validasi metode pembayaran (wajib dipilih)
    if (!paymentMethod) {
      setPaymentMethodError("Pilih metode pembayaran");
      return;
    }
    
    if (!password.trim()) {
      toast.error("Masukkan password untuk konfirmasi");
      return;
    }

    setIsProcessing(true);
    setPaymentMethodError("");
    try {
      // Verify password first
      const phone = (await axios.get(`${API}/user/profile`, { headers: getAuthHeader() })).data.phone;
      await axios.post(`${API}/auth/login`, { phone, password });

      // Process transaction with payment method
      const res = await axios.post(`${API}/user/transaction`, {
        service_type: serviceType,
        customer_no: customerNo,
        customer_name: customerName || "",
        customer_address: customerAddress || "",
        product_name: productName || "",
        amount: amount,
        admin_fee: adminFee || 1500,
        buyer_sku_code: buyerSkuCode || "",
        bill_details: billDetails || [],
        bill_ids: billIds || [],
        digiflazz_ref_id: digiflazzRefId || "",
        payment_method: paymentMethod // "cash" atau "transfer"
      }, { headers: getAuthHeader() });

      localStorage.setItem("user_balance", res.data.balance_after);

      // Determine status based on service type
      const needsSN = serviceType === "token_listrik" || serviceType === "pulsa";
      const hasSN = !!res.data.sn;
      const transactionStatus = needsSN ? (hasSN ? "LUNAS" : "PENDING") : "LUNAS";
      const isPendingTransaction = needsSN && !hasSN;

      // Build receipt data
      const txnData = {
        id: res.data.transaction_id,
        service_type: serviceType,
        service_name: serviceType === "pdam" ? "Tagihan PDAM" : serviceType === "token_listrik" ? "Token Listrik" : serviceType === "tagihan_listrik" ? "Tagihan Listrik" : "Pulsa",
        customer_no: customerNo,
        customer_name: customerName || "",
        customer_address: customerAddress || "",
        product_name: productName || "",
        amount: amount,
        admin_fee: adminFee || 1500,
        total: res.data.total,
        bill_details: billDetails || [],
        cashier_name: userName,
        cashier_role: userRole,
        date: new Date().toLocaleString("id-ID"),
        status: transactionStatus,
        payment_method: paymentMethod, // "cash" atau "transfer"
        sn: res.data.sn || "",
        isPending: isPendingTransaction
      };
      
      // Save receipt data to localStorage
      localStorage.setItem("last_receipt", JSON.stringify(txnData));
      
      // Close confirmation modal
      setShowConfirm(false);
      setPassword("");
      
      if (txnData.isPending) {
        toast.success("Transaksi diproses! SN akan muncul setelah dikonfirmasi DigiFlazz.");
      } else {
        toast.success("Transaksi berhasil!");
      }
      
      if (onSuccess) onSuccess(res.data);
      
      // Redirect to receipt page immediately
      navigate(`/receipt?return=${returnPath || "/bills"}`);
      
    } catch (err) {
      const msg = err.response?.data?.detail || "Transaksi gagal";
      if (msg.includes("password") || msg.includes("salah")) {
        toast.error("Password salah");
      } else {
        toast.error(msg);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Pay Button */}
      <div className="space-y-2">
        {/* Petugas tidak perlu saldo - tampilkan alert hanya untuk non-petugas */}
        {!isPetugas && balance < total && (
          <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>Saldo tidak cukup ({formatCurrency(balance)})</AlertDescription></Alert>
        )}
        <Button
          onClick={() => setShowConfirm(true)}
          disabled={!isPetugas && balance < total}
          className="w-full h-14 rounded-full text-lg font-semibold bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
          data-testid="agent-pay-btn"
        >
          <Wallet className="w-5 h-5 mr-2" /> Bayar {formatCurrency(total)}
        </Button>
        <p className="text-xs text-center text-slate-500">
          {isPetugas ? `Kasir: ${userName}` : `Saldo: ${formatCurrency(balance)} | Kasir: ${userName}`}
        </p>
      </div>

      {/* Password Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { setShowConfirm(false); setPaymentMethod(""); setPaymentMethodError(""); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-5">
              <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-3">
                <Lock className="w-7 h-7 text-sky-600" />
              </div>
              <h3 className="font-heading font-bold text-lg text-slate-800">Konfirmasi Pembayaran</h3>
              <p className="text-sm text-slate-500 mt-1">Total: <span className="font-bold text-sky-600">{formatCurrency(total)}</span></p>
            </div>
            
            {/* Payment Method Selection */}
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Metode Pembayaran <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setPaymentMethod("cash"); setPaymentMethodError(""); }}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    paymentMethod === "cash" 
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700" 
                      : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
                  }`}
                  data-testid="agent-payment-method-cash"
                  disabled={isProcessing}
                >
                  <Banknote className="w-5 h-5" />
                  <span className="font-semibold">Cash</span>
                  {paymentMethod === "cash" && <Check className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => { setPaymentMethod("transfer"); setPaymentMethodError(""); }}
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    paymentMethod === "transfer" 
                      ? "border-blue-500 bg-blue-50 text-blue-700" 
                      : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"
                  }`}
                  data-testid="agent-payment-method-transfer"
                  disabled={isProcessing}
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="font-semibold">Transfer</span>
                  {paymentMethod === "transfer" && <Check className="w-4 h-4" />}
                </button>
              </div>
              {paymentMethodError && (
                <p className="text-red-500 text-xs mt-1" data-testid="agent-payment-method-error">{paymentMethodError}</p>
              )}
            </div>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Masukkan Password</label>
              <Input
                type="password"
                placeholder="Password Anda"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12 rounded-xl text-center text-lg"
                onKeyDown={(e) => e.key === "Enter" && handleConfirmPayment()}
                data-testid="agent-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full h-12" onClick={() => { setShowConfirm(false); setPassword(""); setPaymentMethod(""); setPaymentMethodError(""); }}>Batal</Button>
              <Button
                className="flex-1 rounded-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600"
                onClick={handleConfirmPayment}
                disabled={isProcessing}
                data-testid="agent-confirm-btn"
              >
                {isProcessing ? <div className="spinner w-5 h-5" /> : "Konfirmasi"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
}

export function isAgentLoggedIn() {
  return !!localStorage.getItem("user_token");
}
